import axios from 'axios'

import i18n from '@/plugins/i18n'
import { ROOM_DOCUMENT_VIEWER_ROUTE_NAME, ROOM_DOCUMENTS_ROUTE_NAME, ROOM_ROUTE_TYPES } from '@/router/routerData'
import { getRouter } from '@/router/routerInstance'

const signableFileAllowedExtensions = ['pdf']
const QA_IMPORT_ALLOWED_EXTENSIONS = ['.csv', '.xls', '.xlsx']
const FILE_NAME_MAX_LENGTH = 100

export const idCheckFileAllowedExtensions = '.pdf, .jpg, .png'

export const FILE_TYPES = {
  DOCUMENT: 'document',
  TODO: 'todo',
  SIGNED: 'signed',
}

if (import.meta.env.VUE_APP_ENABLE_WORD_SIGNING) {
  signableFileAllowedExtensions.push('doc')
  signableFileAllowedExtensions.push('docx')
}

function isFileLowerThanMaxSignableSize (file, maxSignableSize = 0) {
  // If we don't have a maxSignableSize... We could at least use the max file size.
  const availableSize = maxSignableSize || import.meta.env.VUE_APP_MAX_FILE_SIZE
  return file.size < availableSize * 1024 * 1024
}

function isFileExtensionSignable (file) {
  if (file.extension) {
    return signableFileAllowedExtensions.includes(file.extension)
  } else {
    return signableFileAllowedExtensions.includes(file.name.split('.').pop().toLowerCase())
  }
}

function isFileSignable (file, maxSignableSize) {
  return isFileLowerThanMaxSignableSize(file, maxSignableSize) && isFileExtensionSignable(file)
}

/**
 * Start downloading a file by the browser
 * @param {File} file
 * @return void
 */
function downloadFileObject (file) {
  const blob = new Blob([file], { type: file.type })
  const blobURL = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = blobURL
  link.download = file.name
  document.body.append(link)
  link.click()

  link.remove()
  setTimeout(() => URL.revokeObjectURL(link.href), 7000)
}

function fileNameWithoutExtension (fileName) {
  const nameSegments = fileName.split('.')

  if (nameSegments.length > 1) {
    return nameSegments.slice(0, -1).join('.')
  }

  return fileName
}

function roomFileDownloadUrl (fileId, roomMnemo) {
  return `${axios.defaults.baseURL}/room/${roomMnemo}/downloads/file/${fileId}`
}

function flattenFildersTree (filders) {
  const flattenFolderChildren = folder => {
    const childrenFiles = []

    if (folder.hasOwnProperty('children')) {
      for (const child of folder.children) {
        childrenFiles.push(...flattenFolderChildren(child))
      }
    } else {
      childrenFiles.push(folder)
    }
    return childrenFiles
  }

  const files = []
  filders.map(filder => {
    if (filder.type === 'folder') {
      files.push(...flattenFolderChildren(filder))
    } else {
      files.push(filder)
    }
  })

  return files
}

/**
 * Format a size in bytes into a human-readable string (o / Ko / Mo / Go).
 * @param {number} size - size in bytes
 * @return {string}
 */
function formatFileSize (size) {
  if (size < 1024) {
    return size + ' ' + i18n.global.t('design-system.display.FileSizeDisplay.lblB')
  } else if (size < 1024 * 1024) {
    return (size / 1024).toFixed(0) + ' ' + i18n.global.t('design-system.display.FileSizeDisplay.lblKB')
  } else if (size < 1024 * 1024 * 1024) {
    return (size / (1024 * 1024)).toFixed(1) + ' ' + i18n.global.t('design-system.display.FileSizeDisplay.lblMB')
  } else {
    return (size / (1024 * 1024 * 1024)).toFixed(2) + ' ' + i18n.global.t('design-system.display.FileSizeDisplay.lblGB')
  }
}

function getFileExtension (file) {
  let ext = null
  if (file.ext) {
    ext = file.ext
  } else if (file.docExt) {
    ext = file.docExt
  } else if (file.extension) {
    ext = file.extension
  } else if (file.name?.includes('.')) {
    ext = file.name.split('.').pop()
  }
  // Special cases where we can't deduct the extension and it has to be contextually applied
  // E.g In case of bibles
  if (!ext) {
    if (file.bibleId) {
      ext = 'zip'
    }
  }
  return ext
}
/**
 * Open a document viewer in a new tab
 * @param {string} roomMnemo
 * @param {number} documentId
 * @param {object} query - optional query params
 */
function openFileViewer(roomMnemo, documentId, query = {}) {
  const route = getRouter().resolve({
    name: ROOM_DOCUMENT_VIEWER_ROUTE_NAME,
    params: { mnemo: roomMnemo, fileId: documentId },
    query,
  })
  window.open(route.href, '_blank')
}

function getRoomRouteTypeFromType(roomTypeType) {
  if (roomTypeType?.toLowerCase().includes('dataroom')) {
    return ROOM_ROUTE_TYPES.DATAROOM
  }
  return ROOM_ROUTE_TYPES.PROJECT
}

function openDocumentsFolder(folderId, mnemo, roomTypeType, fileId) {
  getRouter().push({
    name: ROOM_DOCUMENTS_ROUTE_NAME,
    params: {
      mnemo,
      roomType: getRoomRouteTypeFromType(roomTypeType),
    },
    query: {
      displayedFile: fileId,
      displayedFolder: folderId,
    },
  })
}

export {
  QA_IMPORT_ALLOWED_EXTENSIONS,
  FILE_NAME_MAX_LENGTH,
  isFileLowerThanMaxSignableSize,
  isFileExtensionSignable,
  isFileSignable,
  downloadFileObject,
  fileNameWithoutExtension,
  formatFileSize,
  flattenFildersTree,
  roomFileDownloadUrl,
  getFileExtension,
  openFileViewer,
  openDocumentsFolder,
  getRoomRouteTypeFromType,
}
